/**
 * Shared utility functions for session-related functionality
 * Used by both dictation.tsx and conversation.tsx pages
 */

import type { Patient } from '@/types/session';

/**
 * Format seconds into MM:SS string
 */
export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format Icelandic national ID (kennitala) with dash
 * e.g., "1234567890" -> "123456-7890"
 */
export function formatSSN(nationalId: string): string {
  if (nationalId.length === 10) {
    return `${nationalId.slice(0, 6)}-${nationalId.slice(6)}`;
  }
  return nationalId;
}

/**
 * Extract date of birth from Icelandic national ID
 * e.g., "1234567890" -> "12.34.56"
 */
export function extractDOB(nationalId: string): string {
  if (nationalId.length >= 6) {
    const dd = nationalId.slice(0, 2);
    const mm = nationalId.slice(2, 4);
    const yy = nationalId.slice(4, 6);
    return `${dd}.${mm}.${yy}`;
  }
  return '';
}

/**
 * Search patients in National Registry by birthdate prefix
 */
export async function searchPatientsInRegistry(query: string): Promise<Patient[]> {
  if (!query || query.trim().length < 4) {
    return [];
  }

  // Remove any non-numeric characters from query
  const cleanQuery = query.replace(/\D/g, '');

  if (cleanQuery.length < 4) {
    return [];
  }

  // Use first 4-6 digits for birthdate API call
  const birthdate = cleanQuery.slice(0, 6);

  const response = await fetch(`/api/national-registry/people?birthdate=${birthdate}`);

  if (!response.ok) {
    console.error('National Registry search failed:', response.status);
    return [];
  }

  const data = await response.json();

  // Transform the response to match our Patient interface
  let allPeople: Patient[] = [];

  // Handle both array and {people: [...]} formats
  interface NationalRegistryPerson {
    national_id: string;
    name: string;
    address?: string;
    postal_code?: string;
    city?: string;
    country?: string;
  }
  const peopleArray = Array.isArray(data) ? data : data.people;
  if (Array.isArray(peopleArray)) {
    allPeople = peopleArray.map((person: NationalRegistryPerson) => ({
      id: person.national_id,
      name: person.name,
      ssn: formatSSN(person.national_id),
      dob: extractDOB(person.national_id),
      address: person.address,
      postalCode: person.postal_code ? Number(person.postal_code) : undefined,
    }));
  }

  // Client-side filter: match against the full SSN prefix the user typed
  const filteredPatients = allPeople.filter((patient) => {
    const patientSSN = patient.id.replace(/\D/g, '');
    return patientSSN.startsWith(cleanQuery);
  });

  return filteredPatients;
}

/**
 * Resolve patient SSN to database ID by finding or creating the patient in transcriber_db.
 * This ensures the patient has a valid database ID before starting a session.
 *
 * @param patient The patient object from National Registry (with SSN as id)
 * @returns The patient object with dbId populated, or the original if resolution fails
 */
export async function resolvePatientDbId(patient: Patient): Promise<Patient> {
  // If patient already has a dbId, return as-is
  if (patient.dbId) {
    return patient;
  }

  try {
    // Extract date of birth in ISO format from SSN for patient creation
    const ssn = patient.id.replace(/\D/g, '');
    let dateOfBirth: string | undefined;

    if (ssn.length >= 6) {
      const dd = ssn.slice(0, 2);
      const mm = ssn.slice(2, 4);
      const yy = ssn.slice(4, 6);
      // Determine century: 00-30 = 2000s, 31-99 = 1900s (simplified heuristic)
      const century = Number.parseInt(yy, 10) <= 30 ? '20' : '19';
      dateOfBirth = `${century}${yy}-${mm}-${dd}`;
    }

    const response = await fetch('/api/patients/find-or-create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        ssn: ssn,
        name: patient.name,
        date_of_birth: dateOfBirth,
      }),
    });

    if (response.ok) {
      const data = await response.json();
      return {
        ...patient,
        dbId: data.id,
      };
    }
    console.error('[Patient] Failed to resolve database ID:', response.status);
    return patient;
  } catch (error) {
    console.error('[Patient] Error resolving database ID:', error);
    return patient;
  }
}
